/**
 * IndexedDB — ไม่ใช้ localStorage เพราะ review log โตเรื่อยๆ และ localStorage เขียนแบบ sync (บล็อก UI)
 *
 * เก็บเฉพาะสิ่งที่ผู้ใช้สร้าง: ความคืบหน้า, ประวัติ, mnemonic ที่แต่งเอง
 * ตัวการ์ดของ deck สำเร็จรูปอ่านจากโมดูล — อัปเดตเนื้อหาแล้วความจำไม่หาย
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { CardId, DeckId, DrillStat, Progress, ReviewLog } from './types'

const DB_NAME = 'jpt'
const DB_VERSION = 1

/** ข้อความที่แต่งเอง — แยกจาก Card เพื่อให้อัปเดต deck ทับได้ */
export interface CardOverride {
  cardId: CardId
  keyword?: string
  mnemonic?: string
  updatedAt: number
}

interface JPTSchema extends DBSchema {
  progress: {
    key: CardId
    value: Progress
    indexes: { 'by-deck': DeckId; 'by-due': number }
  }
  log: {
    key: number
    value: ReviewLog
    indexes: { 'by-card': CardId; 'by-ts': number }
  }
  drill: {
    key: CardId
    value: DrillStat
  }
  overrides: {
    key: CardId
    value: CardOverride
  }
  meta: {
    key: string
    value: { key: string; value: unknown }
  }
}

let dbp: Promise<IDBPDatabase<JPTSchema>> | null = null

export function db(): Promise<IDBPDatabase<JPTSchema>> {
  dbp ??= openDB<JPTSchema>(DB_NAME, DB_VERSION, {
    upgrade(d) {
      const progress = d.createObjectStore('progress', { keyPath: 'cardId' })
      progress.createIndex('by-deck', 'deck')
      progress.createIndex('by-due', 'due')

      const log = d.createObjectStore('log', { keyPath: 'id', autoIncrement: true })
      log.createIndex('by-card', 'cardId')
      log.createIndex('by-ts', 'ts')

      d.createObjectStore('drill', { keyPath: 'cardId' })
      d.createObjectStore('overrides', { keyPath: 'cardId' })
      d.createObjectStore('meta', { keyPath: 'key' })
    },
  })
  return dbp
}

// ---------- progress ----------

export async function getProgressByDeck(deck: DeckId): Promise<Progress[]> {
  return (await db()).getAllFromIndex('progress', 'by-deck', deck)
}

export async function putProgress(p: Progress): Promise<void> {
  await (await db()).put('progress', p)
}

export async function putProgressBulk(list: Progress[]): Promise<void> {
  const d = await db()
  const tx = d.transaction('progress', 'readwrite')
  await Promise.all([...list.map((p) => tx.store.put(p)), tx.done])
}

// ---------- log ----------

export async function addLog(entry: ReviewLog): Promise<void> {
  await (await db()).add('log', entry)
}

export async function getLogSince(ts: number): Promise<ReviewLog[]> {
  return (await db()).getAllFromIndex('log', 'by-ts', IDBKeyRange.lowerBound(ts))
}

// ---------- drill (kana) ----------

export async function getDrillAll(): Promise<DrillStat[]> {
  return (await db()).getAll('drill')
}

export async function putDrill(s: DrillStat): Promise<void> {
  await (await db()).put('drill', s)
}

// ---------- overrides ----------

export async function getOverride(id: CardId): Promise<CardOverride | undefined> {
  return (await db()).get('overrides', id)
}

export async function getOverrideAll(): Promise<CardOverride[]> {
  return (await db()).getAll('overrides')
}

export async function putOverride(o: CardOverride): Promise<void> {
  await (await db()).put('overrides', o)
}

// ---------- meta ----------

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await (await db()).get('meta', key)
  return row?.value as T | undefined
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', { key, value })
}

/** ล้างทุกอย่าง — ใช้ตอนกดรีเซ็ต ต้องถามยืนยันก่อนเรียก */
export async function clearAll(): Promise<void> {
  const d = await db()
  const tx = d.transaction(['progress', 'log', 'drill', 'overrides', 'meta'], 'readwrite')
  await Promise.all([
    tx.objectStore('progress').clear(),
    tx.objectStore('log').clear(),
    tx.objectStore('drill').clear(),
    tx.objectStore('overrides').clear(),
    tx.objectStore('meta').clear(),
    tx.done,
  ])
}
